import { createContext, ReactNode, useCallback, useMemo, useRef, useState } from 'react';
import { ConfirmModal, ConfirmModalProps } from './ConfirmModal';

export const ConfirmModalContext = createContext<{
  confirm: <T extends object, D extends object>(
    data: Partial<ConfirmModalProps<T, D>> & {
      postConfirmationDialogProps?: Partial<ConfirmModalProps<object, object>>;
    }
  ) => Promise<unknown>;
}>({
  confirm: () => Promise.resolve()
});

export function ConfirmModalProvider({ children }: { children: ReactNode }) {
  const [confirmModalProps, setConfirmModalProps] = useState<
    Partial<ConfirmModalProps<object, object>>
  >({
    open: false
  });
  const onConfirmDialogResponseRef = useRef<
    (resp: unknown | boolean, positiveEvent: boolean) => void
  >(() => {});
  const postConfirmationDialogShownRef = useRef(false);

  const confirm = useCallback(
    <T extends object, D extends object>(
      data: Partial<ConfirmModalProps<T, D>> & {
        postConfirmationDialogProps?: Partial<ConfirmModalProps<object, object>>;
      }
    ) =>
      new Promise<unknown | boolean>(resolve => {
        setConfirmModalProps({ ...data, open: true } as ConfirmModalProps<object, object>);

        onConfirmDialogResponseRef.current = (resp: unknown | boolean, positiveEvent: boolean) => {
          // all postConfirmation action handler will handled by their own callback
          resolve(!positiveEvent ? false : resp);

          if (data.postConfirmationDialogProps && resp && !postConfirmationDialogShownRef.current) {
            postConfirmationDialogShownRef.current = true;
            setConfirmModalProps({
              ...data.postConfirmationDialogProps,
              open: true
            });
          } else if (
            data.postConfirmationDialogProps &&
            postConfirmationDialogShownRef.current &&
            typeof data.postConfirmationDialogProps.onClose === 'function' &&
            resp
          ) {
            postConfirmationDialogShownRef.current = false;
            data.postConfirmationDialogProps.onClose(resp);
            setConfirmModalProps(s => ({ ...s, open: false }));
          } else {
            setConfirmModalProps(s => ({ ...s, open: false }));
          }
        };
      }),
    [setConfirmModalProps]
  );

  const { open, ...others } = confirmModalProps;

  const memoizedConfirm = useMemo(() => ({ confirm }), [confirm]);

  return (
    <ConfirmModalContext.Provider value={memoizedConfirm}>
      {children}

      <ConfirmModal
        {...(others as ConfirmModalProps<object, object>)}
        open={open}
        onClose={resp => onConfirmDialogResponseRef.current(resp, false)}
        onPositiveClickSuccess={resp => onConfirmDialogResponseRef.current(resp, true)}
      />
    </ConfirmModalContext.Provider>
  );
}
